import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Users, 
  Code, 
  Shield, 
  Palette,
  BarChart3,
  Globe,
  ArrowRight,
  CheckCircle,
  Star,
  Zap,
  Heart,
  Coffee,
  Gamepad2,
  BookOpen,
  Trophy,
  Briefcase
} from 'lucide-react'

const Careers: React.FC = () => {
  const positions = [
    {
      title: '区块链开发工程师',
      department: '技术部',
      location: '北京/上海/深圳',
      type: '全职',
      experience: '3-5年',
      salary: '25-40K',
      description: '负责区块链底层技术开发，智能合约编写，DeFi协议开发',
      requirements: [
        '熟悉Solidity、Rust等区块链开发语言',
        '有DeFi、NFT项目开发经验',
        '了解Layer2、跨链技术',
        '具备良好的代码规范和文档习惯'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '技术培训']
    },
    {
      title: '硬件工程师',
      department: '硬件部',
      location: '深圳',
      type: '全职',
      experience: '5-8年',
      salary: '30-50K',
      description: '负责CoreTrust手机硬件设计，安全芯片集成，硬件钱包开发',
      requirements: [
        '熟悉ARM架构和移动设备硬件设计',
        '有安全芯片、加密硬件开发经验',
        '了解硬件安全标准和认证流程',
        '具备PCB设计和调试能力'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '技术培训']
    },
    {
      title: '产品经理',
      department: '产品部',
      location: '北京',
      type: '全职',
      experience: '3-5年',
      salary: '20-35K',
      description: '负责Web3产品规划，用户体验设计，产品迭代管理',
      requirements: [
        '有Web3/区块链产品经验',
        '熟悉产品设计流程和方法论',
        '具备良好的用户洞察能力',
        '有技术背景优先'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '产品培训']
    },
    {
      title: 'UI/UX设计师',
      department: '设计部',
      location: '北京/上海',
      type: '全职',
      experience: '2-4年',
      salary: '15-25K',
      description: '负责产品界面设计，用户体验优化，设计系统建设',
      requirements: [
        '熟练使用Figma、Sketch等设计工具',
        '有移动端产品设计经验',
        '了解Web3产品设计特点',
        '具备良好的审美和设计能力'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '设计培训']
    },
    {
      title: '市场推广经理',
      department: '市场部',
      location: '北京',
      type: '全职',
      experience: '3-5年',
      salary: '18-30K',
      description: '负责品牌推广，市场活动策划，合作伙伴关系维护',
      requirements: [
        '有科技产品市场推广经验',
        '了解Web3/区块链行业',
        '具备良好的沟通和协调能力',
        '有媒体资源优先'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '市场培训']
    },
    {
      title: '社区运营',
      department: '运营部',
      location: '北京/远程',
      type: '全职',
      experience: '1-3年',
      salary: '12-20K',
      description: '负责社区建设，用户运营，内容创作，活动策划',
      requirements: [
        '有社区运营或内容创作经验',
        '了解Web3/区块链文化',
        '具备良好的文字表达能力',
        '有社交媒体运营经验'
      ],
      benefits: ['五险一金', '股权激励', '弹性工作', '运营培训']
    }
  ]

  const benefits = [
    { icon: Heart, title: '健康保障', description: '五险一金 + 商业保险' },
    { icon: Coffee, title: '工作环境', description: '舒适办公 + 免费咖啡' },
    { icon: Gamepad2, title: '娱乐设施', description: '游戏室 + 健身房' },
    { icon: BookOpen, title: '学习成长', description: '培训基金 + 技术分享' },
    { icon: Trophy, title: '激励机制', description: '股权激励 + 年终奖' },
    { icon: Briefcase, title: '职业发展', description: '晋升通道 + 导师制度' }
  ]

  const culture = [
    {
      title: '创新驱动',
      description: '鼓励创新思维，支持技术探索',
      icon: Zap
    },
    {
      title: '团队协作',
      description: '开放沟通，互相支持',
      icon: Users
    },
    {
      title: '持续学习',
      description: '终身学习，不断成长',
      icon: BookOpen
    },
    {
      title: '用户至上',
      description: '以用户需求为导向',
      icon: Heart
    }
  ]

  return (
    <>
      <Helmet>
        <title>人才招聘 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited招聘信息，包括技术、产品、设计、市场等岗位，提供有竞争力的薪酬和福利。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                加入我们
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                与我们一起构建Web3的未来，创造改变世界的产品
              </p>
            </motion.div>
          </div>
        </section>

        {/* Culture Section */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                企业文化
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                我们相信，优秀的企业文化是成功的关键
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {culture.map((item, index) => {
                const Icon = item.icon
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                员工福利
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                我们为员工提供全方位的福利保障，让工作更舒心
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon
                return (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {benefit.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Positions Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                招聘职位
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                我们正在寻找志同道合的伙伴，一起创造Web3的未来
              </p>
            </motion.div>

            <div className="space-y-8">
              {positions.map((position, index) => (
                <motion.div
                  key={position.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                    <div className="mb-4 lg:mb-0">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {position.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-1" />
                          {position.department}
                        </span>
                        <span className="flex items-center">
                          <Globe className="w-4 h-4 mr-1" />
                          {position.location}
                        </span>
                        <span className="flex items-center">
                          <Star className="w-4 h-4 mr-1" />
                          {position.type}
                        </span>
                        <span className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {position.experience}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                        {position.salary}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        月薪
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {position.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        任职要求：
                      </h4>
                      <ul className="space-y-2">
                        {position.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        福利待遇：
                      </h4>
                      <ul className="space-y-2">
                        {position.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Star className="w-4 h-4 text-yellow-500 mr-2" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Link
                      to={`/careers/apply/${index}`}
                      className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
                    >
                      <span>立即投递</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                没有找到合适的职位？
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                我们欢迎有才华的人才加入，即使暂时没有合适的职位，也欢迎投递简历
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center space-x-2">
                  <span>投递简历</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-colors duration-300">
                  联系我们
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Careers